import React, { Component } from 'react'
import { compose } from 'redux'
import { arrayOf, bool, func, shape, string } from 'prop-types'
import algoliasearch from 'algoliasearch'
import classNames from 'classnames'
import { Trans } from '@lingui/macro'

import { subscribe } from 'shared/store'
import { QUERY_UPDATED } from 'shared/store/ducks/events'

import { publicRuntimeConfigType, mountPointsType } from 'views/utils/propTypes'

import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import withMountPoints from 'views/enhancers/withMountPoints'
import algoliaUserService from 'views/assets/scripts/services/storage/AlgoliaUserService'
import SearchBoxListItem, {
  renderName
} from 'views/components/organisms/SearchBox/SearchBoxListItem'
import { normalizeUrl } from 'shared/utils/url'
import logger from 'shared/services/logger'
import debounce from 'views/utils/debounce'

import Icon from 'views/components/atoms/Icon'
import Form from 'views/components/atoms/Form'
import Button from 'views/components/atoms/Button'
import ButtonGhost from 'views/components/molecules/ButtonGhost'

import { SELECTOR_DESKTOP_INPUT } from 'views/containers/browser/SearchBoxContainer/consts'

import './AfterQuerySuggestLayer.scss'

const INTERACTIVE_KEYS = [
  'Escape',
  'Esc',
  'ArrowUp',
  'Up',
  'ArrowDown',
  'Down',
  'ArrowRight',
  'Right',
  'ArrowLeft',
  'Left'
]

class AfterQuerySuggestLayer extends Component {
  static DEBOUNCE_TIME = 400

  constructor(props) {
    super(props)

    this.state = {
      suggestions: [],
      highlighted: -1
    }

    this.index = this.getIndex()
  }

  componentDidMount() {
    const { query } = this.props
    this.updateSuggestions(query)

    subscribe.after(QUERY_UPDATED, ({ updatedQuery }) => {
      debounce(
        () => this.updateSuggestions(updatedQuery),
        AfterQuerySuggestLayer.DEBOUNCE_TIME
      )()
    })

    const desktopInput = document.querySelector(SELECTOR_DESKTOP_INPUT)

    if (desktopInput) {
      desktopInput.addEventListener('keyup', this.onKeyPressed)

      const desktopForm = desktopInput.closest('form')
      desktopForm.addEventListener('submit', this.onSubmit)
    }
  }

  onSubmit = () => {
    const { suggestions, highlighted } = this.state
    const { products, mountPoints, query } = this.props
    const allItems = suggestions.concat(products)

    if (highlighted >= 0 && allItems?.[highlighted]) {
      window.location.href = normalizeUrl(allItems[highlighted].deeplink)
      return
    }

    window.location.href = mountPoints.getSearch({
      query,
      userToken: algoliaUserService.getToken()
    })
  }

  getIndex = () => {
    const {
      publicRuntimeConfig: {
        publicConfig: { searchConfig }
      }
    } = this.props

    const {
      analytics: { appId, apiKey, indexName }
    } = searchConfig

    try {
      const client = algoliasearch(appId, apiKey)
      return client.initIndex(indexName)
    } catch (error) {
      logger.error(error.message, { error })
    }

    return null
  }

  updateSuggestions = query => {
    if (!this.index) {
      return
    }

    const { mountPoints } = this.props

    this.index.search(query, { hitsPerPage: 4 }).then(({ hits }) => {
      this.setState({
        suggestions: hits.map(hit => ({
          name: hit.query,
          deeplink: mountPoints.getSearch({
            query: hit.query,
            userToken: algoliaUserService.getToken()
          })
        }))
      })
    })
  }

  updateInputValue = value => {
    const { handleValueChange } = this.props

    if (typeof handleValueChange === 'function') {
      handleValueChange(value)
    }
  }

  onKeyPressed = event => {
    const { key } = event

    if (!INTERACTIVE_KEYS.includes(key)) {
      return
    }

    event.preventDefault()

    const { suggestions } = this.state
    const { products } = this.props
    const allItems = suggestions.concat(products)

    switch (key) {
      case 'ArrowUp':
      case 'Up':
        this.onKeyUp(allItems)
        break
      case 'ArrowDown':
      case 'Down':
        this.onKeyDown(allItems)
        break
      case 'ArrowRight':
      case 'Right':
      case 'ArrowLeft':
      case 'Left':
        this.onKeyRightLeft()
        break
      default:
        this.onKeyEsc()
    }
  }

  onKeyUp = allItems => {
    const maxLength = allItems.length
    const { highlighted } = this.state
    const prev = highlighted - 1
    const prevHighlighted = prev >= 0 ? prev : maxLength - 1

    if (allItems?.[prevHighlighted]) {
      this.updateInputValue(allItems[prevHighlighted].name)
    }

    this.setState({
      highlighted: prevHighlighted
    })
  }

  onKeyDown = allItems => {
    const { highlighted } = this.state

    const maxLength = allItems.length
    const next = highlighted + 1
    const nextHighlighted = next < maxLength ? next : 0

    if (allItems?.[nextHighlighted]) {
      this.updateInputValue(allItems[nextHighlighted].name)
    }

    this.setState({
      highlighted: nextHighlighted
    })
  }

  onKeyRightLeft = () => {
    const { twoColumns, products } = this.props
    const { suggestions, highlighted } = this.state

    if (
      !twoColumns ||
      highlighted < 0 ||
      !suggestions?.length ||
      !products?.length
    ) {
      return
    }

    const leftSideActive = highlighted < suggestions.length
    this.setState({
      highlighted: leftSideActive ? suggestions.length : 0
    })
  }

  onKeyEsc = () => {
    const { hideResults } = this.props

    if (typeof hideResults === 'function') {
      hideResults()
    }
  }

  render() {
    const {
      products,
      mobile,
      handleItemClick,
      handleClick,
      formAction,
      twoColumns,
      query,
      displayResults
    } = this.props
    const { suggestions, highlighted } = this.state

    if (!displayResults) {
      return null
    }

    const suggestionsList =
      suggestions?.length > 0 ? (
        <ul className="a-list-reset">
          {suggestions.map((suggestion, index) => (
            <li
              className="l-flex e-AfterQuerySuggestLayer--suggestion-item"
              key={suggestion.name}
            >
              <a
                className={classNames('a-link', {
                  'a-link--underlined': index === highlighted
                })}
                href={suggestion.deeplink}
                data-clientside-hook="SearchBoxListSuggestLink"
              >
                <Icon
                  icon="search"
                  size="small"
                  className="u-color--dark u-margin-x-small--right"
                />
                {renderName({
                  name: suggestion.name,
                  queryStr: query,
                  concat: false
                })}
              </a>
            </li>
          ))}
        </ul>
      ) : null

    const ButtonComponent = mobile ? Button : ButtonGhost
    const productList = (
      <>
        <ul className="a-list-reset o-SearchBox__list u-margin--bottom u-margin-small--bottom@medium">
          {products.map((product, index) => (
            <SearchBoxListItem
              mobile={mobile}
              queryStr={query}
              key={product.id}
              handleItemClick={handleItemClick}
              item={product}
              highlighted={highlighted === index + suggestions.length}
              last={index + 1 === products.length}
            />
          ))}
        </ul>
        <div className="o-SearchBox__controls u-text-align--center">
          <Form name="search-results" method="get" action={formAction}>
            <ButtonComponent
              name="show-all-results-button"
              onClick={handleClick}
            >
              <Trans id="general.label.showAllResults" />
            </ButtonComponent>
          </Form>
        </div>
      </>
    )

    if (twoColumns && suggestionsList) {
      return (
        <div className="l-flex l-flex--stretch-children l-flex--wrap">
          <div className="l-col--12-of-12 l-col--4-of-12@medium u-background-color--greySky u-padding--top u-padding--bottom u-padding--sides@medium u-border--bottom@small-tiny u-margin--left@small-tiny u-margin--right@small-tiny">
            {suggestionsList}
          </div>
          <div className="l-col--12-of-12 l-col--8-of-12@medium">
            <div className="u-padding--sides u-padding--bottom u-padding-x-small--top">
              {productList}
            </div>
          </div>
        </div>
      )
    }

    return (
      <>
        {suggestionsList && (
          <div className="u-border--bottom u-padding--ends u-margin--left u-margin--right">
            {suggestionsList}
          </div>
        )}
        <div
          className={classNames('u-padding--sides u-padding--bottom', {
            'u-padding-x-small--top@small-tiny': suggestionsList,
            'u-padding--top': !suggestionsList
          })}
        >
          {productList}
        </div>
      </>
    )
  }
}

AfterQuerySuggestLayer.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  mountPoints: mountPointsType.isRequired,
  query: string,
  products: arrayOf(SearchBoxListItem.propTypes.item),
  mobile: bool,
  handleItemClick: func,
  handleClick: func,
  formAction: string.isRequired,
  hideResults: func,
  twoColumns: bool,
  handleValueChange: func,
  displayResults: bool
}

AfterQuerySuggestLayer.defaultProps = {
  query: '',
  products: [],
  mobile: false,
  handleItemClick: null,
  handleClick: null,
  hideResults: null,
  twoColumns: false,
  handleValueChange: null,
  displayResults: false
}

export default compose(
  withShopAndRuntimeConfig,
  withMountPoints
)(AfterQuerySuggestLayer)
